/**
 * Service for generating MapLibre markers with custom colors and Lucide icons
 * Follows Lucide accessibility best practices: https://lucide.dev/guide/advanced/accessibility
 *
 * Fetches Lucide icons via Iconify API, e.g. https://api.iconify.design/lucide/droplet.svg
 * (with optional ?color= for tint). Icon set: https://lucide.dev/icons/
 */
export class MarkerGeneratorService {
  private static iconCache: globalThis.Map<string, string> = new globalThis.Map()

  /** Clears the in-memory SVG marker cache (call when indicator / icon config changes). */
  static clearCache(): void {
    this.iconCache.clear()
  }

  // Pin SVG path data (MapLibre default pin shape)
  private static readonly PIN_PATH =
    'M 5,33.103579 C 5,17.607779 18.457,5 35,5 C 51.543,5 65,17.607779 65,33.103579 C 65,56.388679 40.4668,76.048179 36.6112,79.137779 C 36.3714,79.329879 36.2116,79.457979 36.1427,79.518879 C 35.8203,79.800879 35.4102,79.942779 35,79.942779 C 34.5899,79.942779 34.1797,79.800879 33.8575,79.518879 C 33.7886,79.457979 33.6289,79.330079 33.3893,79.138079 C 29.5346,76.049279 5,56.389379 5,33.103579 Z'

  /** Inline path data for common icons when Iconify fetch fails (e.g. offline or CORS). Lucide 24x24. */
  private static readonly FALLBACK_ICONS: Record<
    string,
    { viewBoxWidth: number; viewBoxHeight: number; paths: string[] }
  > = {
    droplet: {
      viewBoxWidth: 24,
      viewBoxHeight: 24,
      paths: [
        'M12 22a7 7 0 0 0 7-7c0-2-1-3.9-3-5.5s-3.5-4-4-6.5c-.5 2.5-2 4.9-4 6.5C6 11.1 5 13 5 15a7 7 0 0 0 7 7z',
      ],
    },
    'tower-control': {
      viewBoxWidth: 24,
      viewBoxHeight: 24,
      paths: [
        'M18 2c0 1.5-.5 2-2 2H8c-1.5 0-2-.5-2-2',
        'M9 22V12h6v10',
        'M8 6h8l-1 4H9L8 6z',
        'M12 12v10',
        'M15 6l1 4',
        'M8 10l1 4',
      ],
    },
    'circle-dot': {
      viewBoxWidth: 24,
      viewBoxHeight: 24,
      paths: [
        'M12 22c5.523 0 10-4.477 10-10S17.523 2 12 2 2 6.477 2 12s4.477 10 10 10z',
        'M12 12m-1 0a1 1 0 1 0 2 0a1 1 0 1 0 -2 0',
      ],
    },
  }

  /**
   * Darkens a hex color by a given percentage
   * @param hexColor Hex color string (e.g., '#0000ff' or '#000')
   * @param percent Percentage to darken (0-100, default: 30)
   * @returns Darkened hex color string
   */
  private static darkenColor(hexColor: string, percent: number = 30): string {
    // Remove # if present
    let hex = hexColor.replace('#', '')

    // Expand short hex to long hex (e.g., '000' -> '000000')
    if (hex.length === 3) {
      hex = hex
        .split('')
        .map((char) => char + char)
        .join('')
    }

    // Parse RGB values
    const r = parseInt(hex.substring(0, 2), 16)
    const g = parseInt(hex.substring(2, 4), 16)
    const b = parseInt(hex.substring(4, 6), 16)

    // Darken by reducing each component
    const factor = 1 - percent / 100
    const newR = Math.max(0, Math.floor(r * factor))
    const newG = Math.max(0, Math.floor(g * factor))
    const newB = Math.max(0, Math.floor(b * factor))

    // Convert back to hex
    const toHex = (n: number) => {
      const hex = n.toString(16)
      return hex.length === 1 ? '0' + hex : hex
    }

    return `#${toHex(newR)}${toHex(newG)}${toHex(newB)}`
  }

  /**
   * Extracts viewBox dimensions from SVG string
   * @param svg SVG string
   * @returns Object with width and height, or null if not found
   */
  private static extractViewBox(svg: string): { width: number; height: number } | null {
    const viewBoxMatch = svg.match(/viewBox="([^"]*)"/)
    if (!viewBoxMatch) {
      return null
    }

    const viewBoxParts = viewBoxMatch[1].split(/\s+/)
    if (viewBoxParts.length < 4) {
      return null
    }

    const width = parseFloat(viewBoxParts[2])
    const height = parseFloat(viewBoxParts[3])

    if (isNaN(width) || isNaN(height)) {
      return null
    }

    return { width, height }
  }

  /**
   * Converts circle attributes to SVG path d (for icons that use <circle> instead of <path>).
   */
  private static circleToPathD(cx: number, cy: number, r: number): string {
    return `M ${cx + r} ${cy} A ${r} ${r} 0 0 1 ${cx} ${cy + r} A ${r} ${r} 0 0 1 ${cx - r} ${cy} A ${r} ${r} 0 0 1 ${cx} ${cy - r} A ${r} ${r} 0 0 1 ${cx + r} ${cy}`
  }

  /** Converts <rect> attributes to a path d string. */
  private static rectToPathD(
    x: number, y: number, w: number, h: number, rx: number = 0, ry: number = 0,
  ): string {
    const r = Math.min(rx || 0, w / 2, ry || rx || 0, h / 2)
    if (r <= 0) return `M ${x} ${y} h ${w} v ${h} h ${-w} Z`
    return [
      `M ${x + r} ${y}`, `h ${w - 2 * r}`,
      `a ${r} ${r} 0 0 1 ${r} ${r}`, `v ${h - 2 * r}`,
      `a ${r} ${r} 0 0 1 ${-r} ${r}`, `h ${-(w - 2 * r)}`,
      `a ${r} ${r} 0 0 1 ${-r} ${-r}`, `v ${-(h - 2 * r)}`,
      `a ${r} ${r} 0 0 1 ${r} ${-r}`, 'Z',
    ].join(' ')
  }

  /** Converts a points string (polyline / polygon) to a path d string. */
  private static pointsToPathD(points: string, close: boolean = false): string {
    const coords = points.trim().split(/[\s,]+/)
    const pairs: string[] = []
    for (let i = 0; i + 1 < coords.length; i += 2) {
      pairs.push(`${coords[i]} ${coords[i + 1]}`)
    }
    if (pairs.length === 0) return ''
    const d = `M ${pairs[0]}${pairs.length > 1 ? ` L ${pairs.slice(1).join(' L ')}` : ''}`
    return close ? `${d} Z` : d
  }

  /**
   * Builds SVG path element strings from a lucide-vue-next __iconNode array.
   * Handles path, circle, rect, line, polyline, polygon elements.
   */
  private static iconNodesToPathElements(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    nodes: Array<[string, Record<string, any>]>,
    iconColor: string,
  ): string {
    const stroke = `fill="none" stroke="${iconColor}" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"`
    const parts: string[] = []
    for (const [tag, attrs] of nodes) {
      if (!attrs) continue
      if (tag === 'path' && attrs.d) {
        parts.push(`<path d="${attrs.d}" ${stroke}/>`)
      } else if (tag === 'circle') {
        const d = this.circleToPathD(Number(attrs.cx), Number(attrs.cy), Number(attrs.r))
        parts.push(`<path d="${d}" ${stroke}/>`)
      } else if (tag === 'rect') {
        const d = this.rectToPathD(
          Number(attrs.x ?? 0), Number(attrs.y ?? 0),
          Number(attrs.width), Number(attrs.height),
          Number(attrs.rx ?? 0), Number(attrs.ry ?? attrs.rx ?? 0),
        )
        parts.push(`<path d="${d}" ${stroke}/>`)
      } else if (tag === 'line') {
        parts.push(`<path d="M ${attrs.x1} ${attrs.y1} L ${attrs.x2} ${attrs.y2}" ${stroke}/>`)
      } else if ((tag === 'polyline' || tag === 'polygon') && attrs.points) {
        const d = this.pointsToPathD(String(attrs.points), tag === 'polygon')
        if (d) parts.push(`<path d="${d}" ${stroke}/>`)
      }
    }
    return parts.join('\n    ')
  }

  /**
   * Resolves Lucide icon path elements from the locally installed lucide-vue-next package.
   * Each component exposes __iconNode: Array<[tag, attrs]> — no network request needed.
   * Returns a string of SVG path elements, or null if the icon is not found.
   */
  /**
   * Cached reference to the lucide core module (loaded once on first use).
   * The `lucide` package exports every icon as a plain node array: Array<[tag, attrs]>.
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private static lucideModuleCache: Record<string, any> | null = null

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private static async getLucideModule(): Promise<Record<string, any>> {
    if (!this.lucideModuleCache) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      this.lucideModuleCache = (await import('lucide')) as any
    }
    return this.lucideModuleCache!
  }

  private static async resolveIconFromLocalPackage(
    iconName: string,
    iconColor: string,
  ): Promise<string | null> {
    try {
      const pascalName = iconName
        .split('-')
        .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
        .join('')
      const lucideModule = await this.getLucideModule()
      const nodes = lucideModule[pascalName] as Array<[string, Record<string, string | number>]> | undefined
      if (!Array.isArray(nodes) || nodes.length === 0) {
        console.debug(`[MarkerGeneratorService] resolveIconFromLocalPackage("${iconName}") → NO nodes found for "${pascalName}"`)
        return null
      }
      const pathElements = this.iconNodesToPathElements(nodes, iconColor)
      console.debug(
        `[MarkerGeneratorService] resolveIconFromLocalPackage("${iconName}") → pathElements (${pathElements.length} chars):`,
        pathElements.slice(0, 200),
      )
      return pathElements || null
    } catch (err) {
      console.debug(`[MarkerGeneratorService] icon "${iconName}" not found in lucide package:`, err)
      return null
    }
  }

  /**
   * Extracts path d strings and circle-to-path from icon SVG (Lucide uses both <path> and <circle>).
   */
  private static extractIconPaths(iconSvg: string): string[] {
    const paths: string[] = []

    const pathMatches = iconSvg.matchAll(/<path[^>]*d="([^"]*)"[^>]*>/g)
    for (const match of pathMatches) {
      if (match[1]?.trim()) paths.push(match[1].trim())
    }

    const circleTagRegex = /<circle[^>]*>/g
    let circleMatch: RegExpExecArray | null
    while ((circleMatch = circleTagRegex.exec(iconSvg)) !== null) {
      const tag = circleMatch[0]
      const cxMatch = tag.match(/\bcx="([^"]+)"/)
      const cyMatch = tag.match(/\bcy="([^"]+)"/)
      const rMatch = tag.match(/\br="([^"]+)"/)
      if (cxMatch && cyMatch && rMatch) {
        const cx = parseFloat(cxMatch[1])
        const cy = parseFloat(cyMatch[1])
        const r = parseFloat(rMatch[1])
        if (!isNaN(cx) && !isNaN(cy) && !isNaN(r) && r > 0) {
          paths.push(this.circleToPathD(cx, cy, r))
        }
      }
    }

    return paths
  }

  /**
   * Generates a MapLibre pin marker SVG with a Lucide icon inside
   * @param color Pin color (default: '#0000ff')
   * @param lucideIcon Lucide icon name (e.g., 'droplet', 'waves', 'home') - optional, without 'lucide:' prefix
   * @param iconColor Icon color inside the pin (default: '#ffffff')
   * @param showBadge Whether to show a badge in the top-right corner (default: false)
   * @param badgeColor Badge color (default: '#a7b6c2') - will be overridden by dynamic layer if needed
   * @returns SVG string of the marker
   */
  static async generateMarker(
    color: string = '#0000ff',
    lucideIcon?: string,
    iconColor: string = '#ffffff',
    showBadge: boolean = false,
    badgeColor: string = '#a7b6c2',
  ): Promise<string> {
    const cacheKey = `${color}-${lucideIcon || 'none'}-${iconColor}-${showBadge}-${badgeColor}`
    if (this.iconCache.has(cacheKey)) {
      return this.iconCache.get(cacheKey)!
    }

    // Generate base pin SVG with dynamic viewBox calculation
    // Extract dimensions from the path to calculate viewBox
    const pathBounds = this.calculatePathBounds(this.PIN_PATH)

    // Shadow parameters
    const shadowOffsetX = 2
    const shadowOffsetY = 2
    const shadowBlur = 3
    const shadowOpacity = 0.3
    const padding = shadowBlur + Math.max(shadowOffsetX, shadowOffsetY)

    // Add padding to viewBox to accommodate shadow
    const viewBoxWidth = pathBounds.maxX - pathBounds.minX + padding * 2
    const viewBoxHeight = pathBounds.maxY - pathBounds.minY + padding * 2
    const viewBoxX = pathBounds.minX - padding
    const viewBoxY = pathBounds.minY - padding

    // Shadow filter definition
    const shadowFilter = `<defs>
    <filter id="marker-shadow" x="-50%" y="-50%" width="200%" height="200%">
      <feGaussianBlur in="SourceAlpha" stdDeviation="${shadowBlur}"/>
      <feOffset dx="${shadowOffsetX}" dy="${shadowOffsetY}" result="offsetblur"/>
      <feComponentTransfer>
        <feFuncA type="linear" slope="${shadowOpacity}"/>
      </feComponentTransfer>
      <feMerge>
        <feMergeNode/>
        <feMergeNode in="SourceGraphic"/>
      </feMerge>
    </filter>
  </defs>`

    // Calculate darker color for outline
    const outlineColor = this.darkenColor(color)

    // Badge (pastille) removed from SVG - will be handled by MapLibre symbol layer with dynamic color
    const pinSvg = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="${viewBoxX} ${viewBoxY} ${viewBoxWidth} ${viewBoxHeight}" fill="${color}">
  ${shadowFilter}
  <path stroke="${outlineColor}" stroke-width="2" d="${this.PIN_PATH}" fill="${color}" filter="url(#marker-shadow)"></path>
</svg>`

    // If no Lucide icon is provided, return the pin without icon
    if (!lucideIcon) {
      this.iconCache.set(cacheKey, pinSvg)
      return pinSvg
    }

    try {
      // Clean icon name (remove 'lucide:' prefix if present)
      const iconName = lucideIcon.replace('lucide:', '')
      console.debug(`[MarkerGeneratorService] generateMarker called with lucideIcon="${lucideIcon}" → iconName="${iconName}"`)

      // Priority 1: read icon data from locally installed lucide-vue-next (no network needed).
      // Each lucide-vue-next component exposes __iconNode: Array<[tag, attrs]>.
      const localPathElements = await this.resolveIconFromLocalPackage(iconName, iconColor)
      if (localPathElements) {
        const markerSvg = this.buildMarkerSvgFromPathData(
          pathBounds,
          localPathElements,
          24,
          24,
          color,
          iconColor,
        )
        console.debug(`[MarkerGeneratorService] buildMarkerSvgFromPathData result (first 400 chars):`, markerSvg.slice(0, 400))
        this.iconCache.set(cacheKey, markerSvg)
        return markerSvg
      }

      // Priority 2: Iconify API (network fallback when icon is not in installed package).
      const iconifyUrl = `https://api.iconify.design/lucide/${iconName}.svg?color=${encodeURIComponent(iconColor)}`
      const response = await fetch(iconifyUrl)
      if (!response.ok) {
        throw new Error(`Failed to fetch icon: ${response.statusText}`)
      }

      const iconSvg = await response.text()

      // Extract viewBox from icon SVG to get dimensions
      const iconViewBox = this.extractViewBox(iconSvg)
      const iconWidth = iconViewBox?.width || 24
      const iconHeight = iconViewBox?.height || 24

      // Extract path and circle elements from the icon SVG (Lucide uses both)
      const paths = this.extractIconPaths(iconSvg)

      if (paths.length === 0) {
        throw new Error('Could not extract icon paths')
      }

      const pathElements = paths
        .map(
          (path: string) =>
            `<path d="${path}" fill="none" stroke="${iconColor}" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"/>`,
        )
        .join('')

      const markerSvg = this.buildMarkerSvgFromPathData(
        pathBounds,
        pathElements,
        iconWidth,
        iconHeight,
        color,
        iconColor,
      )

      this.iconCache.set(cacheKey, markerSvg)
      return markerSvg
    } catch (error) {
      console.error(
        `[MarkerGeneratorService] Error generating marker with icon ${lucideIcon}:`,
        error,
      )
      const iconName = lucideIcon?.replace('lucide:', '') ?? ''
      const fallback = iconName ? this.FALLBACK_ICONS[iconName] : undefined
      if (fallback && fallback.paths.length > 0) {
        const pathBounds = this.calculatePathBounds(this.PIN_PATH)
        const pathElements = fallback.paths
          .map(
            (path: string) =>
              `<path d="${path}" fill="none" stroke="${iconColor}" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"/>`,
          )
          .join('')
        const markerSvgFallback = this.buildMarkerSvgFromPathData(
          pathBounds,
          pathElements,
          fallback.viewBoxWidth,
          fallback.viewBoxHeight,
          color,
          iconColor,
        )
        this.iconCache.set(cacheKey, markerSvgFallback)
        return markerSvgFallback
      }
      this.iconCache.set(cacheKey, pinSvg)
      return pinSvg
    }
  }

  /**
   * Builds pin marker SVG from pre-extracted path elements and icon dimensions (used by generateMarker and fallback).
   */
  private static buildMarkerSvgFromPathData(
    pathBounds: { minX: number; minY: number; maxX: number; maxY: number },
    pathElements: string,
    iconWidth: number,
    iconHeight: number,
    color: string,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars -- kept for API consistency with callers
    iconColor: string,
  ): string {
    const shadowOffsetX = 2
    const shadowOffsetY = 2
    const shadowBlur = 3
    const shadowOpacity = 0.3
    const padding = shadowBlur + Math.max(shadowOffsetX, shadowOffsetY)
    const markerViewBoxWidth = pathBounds.maxX - pathBounds.minX + padding * 2
    const markerViewBoxHeight = pathBounds.maxY - pathBounds.minY + padding * 2
    const markerViewBoxX = pathBounds.minX - padding
    const markerViewBoxY = pathBounds.minY - padding
    const shadowFilter = `<defs>
    <filter id="marker-shadow" x="-50%" y="-50%" width="200%" height="200%">
      <feGaussianBlur in="SourceAlpha" stdDeviation="${shadowBlur}"/>
      <feOffset dx="${shadowOffsetX}" dy="${shadowOffsetY}" result="offsetblur"/>
      <feComponentTransfer>
        <feFuncA type="linear" slope="${shadowOpacity}"/>
      </feComponentTransfer>
      <feMerge>
        <feMergeNode/>
        <feMergeNode in="SourceGraphic"/>
      </feMerge>
    </filter>
  </defs>`
    const outlineColor = this.darkenColor(color)
    const pinCenterX = pathBounds.minX + (pathBounds.maxX - pathBounds.minX) / 2
    const circleTop = pathBounds.minY + (pathBounds.maxY - pathBounds.minY) * 0.1
    const circleBottom = pathBounds.minY + (pathBounds.maxY - pathBounds.minY) * 0.4
    const pinCenterY = (circleTop + circleBottom) / 1.5
    const iconCenterX = iconWidth / 2
    const iconCenterY = iconHeight / 2
    const scale = 1.33
    return `<svg xmlns="http://www.w3.org/2000/svg" viewBox="${markerViewBoxX} ${markerViewBoxY} ${markerViewBoxWidth} ${markerViewBoxHeight}" fill="${color}">
  ${shadowFilter}
  <path stroke="${outlineColor}" stroke-width="2" d="${this.PIN_PATH}" fill="${color}" filter="url(#marker-shadow)"></path>
  <g transform="translate(${pinCenterX} ${pinCenterY}) scale(${scale}) translate(-${iconCenterX} -${iconCenterY})">
    ${pathElements}
  </g>
</svg>`
  }

  /**
   * Calculates bounding box of an SVG path
   * @param pathData SVG path data string
   * @returns Bounding box coordinates
   */
  private static calculatePathBounds(pathData: string): {
    minX: number
    minY: number
    maxX: number
    maxY: number
  } {
    const xCoords: number[] = []
    const yCoords: number[] = []

    // Parse path commands to extract coordinates
    // This is a simplified parser for the specific pin path format
    const commands = pathData.match(/[MCLZ][^MCLZ]*/g) || []
    for (const cmd of commands) {
      const coords = cmd.match(/[-+]?[0-9]*\.?[0-9]+/g)?.map(parseFloat) || []
      for (let i = 0; i < coords.length; i += 2) {
        if (coords[i] !== undefined) xCoords.push(coords[i])
        if (coords[i + 1] !== undefined) yCoords.push(coords[i + 1])
      }
    }

    return {
      minX: Math.min(...xCoords),
      minY: Math.min(...yCoords),
      maxX: Math.max(...xCoords),
      maxY: Math.max(...yCoords),
    }
  }

  /**
   * Extracts inner content of root SVG (elements between opening and closing svg tags).
   */
  private static extractSvgInnerContent(svg: string): string {
    const openMatch = svg.match(/<svg[^>]*>/i)
    const closeMatch = svg.match(/<\/svg\s*>/i)
    if (!openMatch || !closeMatch) {
      return svg
    }
    const start = svg.indexOf(openMatch[0]) + openMatch[0].length
    const end = svg.lastIndexOf(closeMatch[0])
    return start < end ? svg.slice(start, end).trim() : svg
  }

  /**
   * Builds a pin marker SVG with an icon embedded inside at 1/10 size.
   * @param iconSvg Raw SVG string of the icon (e.g. from local assets)
   * @param pinColor Pin fill color (default: '#3388ff')
   * @param badgeColor Optional circular badge color placed on the icon
   * @returns Combined SVG string (pin + icon inside)
   */
  static createMarkerSvgWithIcon(
    iconSvg: string,
    pinColor: string = '#3388ff',
    badgeColor?: string,
  ): string {
    const pathBounds = this.calculatePathBounds(this.PIN_PATH)
    const shadowOffsetX = 2
    const shadowOffsetY = 2
    const shadowBlur = 3
    const padding = shadowBlur + Math.max(shadowOffsetX, shadowOffsetY)
    const markerViewBoxWidth = pathBounds.maxX - pathBounds.minX + padding * 2
    const markerViewBoxHeight = pathBounds.maxY - pathBounds.minY + padding * 2
    const markerViewBoxX = pathBounds.minX - padding
    const markerViewBoxY = pathBounds.minY - padding

    const shadowFilter = `<defs>
    <filter id="marker-shadow-picto" x="-50%" y="-50%" width="200%" height="200%">
      <feGaussianBlur in="SourceAlpha" stdDeviation="${shadowBlur}"/>
      <feOffset dx="${shadowOffsetX}" dy="${shadowOffsetY}" result="offsetblur"/>
      <feComponentTransfer>
        <feFuncA type="linear" slope="0.3"/>
      </feComponentTransfer>
      <feMerge>
        <feMergeNode/>
        <feMergeNode in="SourceGraphic"/>
      </feMerge>
    </filter>
  </defs>`

    const outlineColor = this.darkenColor(pinColor)
    const pinCenterX = pathBounds.minX + (pathBounds.maxX - pathBounds.minX) / 2
    const circleTop = pathBounds.minY + (pathBounds.maxY - pathBounds.minY) * 0.1
    const circleBottom = pathBounds.minY + (pathBounds.maxY - pathBounds.minY) * 0.4
    const pinCenterY = (circleTop + circleBottom) / 1.5

    const iconViewBox = this.extractViewBox(iconSvg)
    const iconWidth = iconViewBox?.width ?? 64
    const iconHeight = iconViewBox?.height ?? 64
    // Target rendered icon size in the pin's own coordinate space (~80 units tall), matching the
    // effective size of the lucide-icon treatment (buildMarkerSvgFromPathData, scale 1.33 on a
    // 24-unit icon ≈ 32 units). A fixed multiplicative scale (e.g. 0.08) on the source SVG's own
    // viewBox made the icon shrink to a few units — invisible — whenever that viewBox was small
    // (24x24) or defaulted (64x64); scaling relative to the target size instead keeps the icon
    // visibly sized regardless of the source SVG's native viewBox dimensions.
    const ICON_TARGET_SIZE = 32
    const iconScale = ICON_TARGET_SIZE / Math.max(iconWidth, iconHeight)
    const iconW = iconWidth * iconScale
    const iconH = iconHeight * iconScale
    const iconX = pinCenterX - iconW / 2
    const iconY = pinCenterY - iconH / 2
    const badgeSize = 18
    const badgeRadius = badgeSize / 2
    const badgeX = iconX + iconW - badgeRadius * 0.35
    const badgeY = iconY + badgeRadius * 0.35
    let iconInner = this.extractSvgInnerContent(iconSvg)
    iconInner = iconInner
      .replace(/\bfill="[^"]*"/g, 'fill="#ffffff"')
      .replace(/\bstroke="[^"]*"/g, 'stroke="#ffffff"')
    const iconViewBoxAttr = iconViewBox ? `viewBox="0 0 ${iconWidth} ${iconHeight}"` : ''

    return `<svg xmlns="http://www.w3.org/2000/svg" viewBox="${markerViewBoxX} ${markerViewBoxY} ${markerViewBoxWidth} ${markerViewBoxHeight}">
  ${shadowFilter}
  <path stroke="${outlineColor}" stroke-width="2" d="${this.PIN_PATH}" fill="${pinColor}" filter="url(#marker-shadow-picto)"/>
  <svg x="${iconX}" y="${iconY}" width="${iconW}" height="${iconH}" ${iconViewBoxAttr} preserveAspectRatio="xMidYMid meet" fill="#ffffff" stroke="#ffffff"><g fill="#ffffff" stroke="#ffffff">${iconInner}</g></svg>
  ${badgeColor ? `<circle cx="${badgeX}" cy="${badgeY}" r="${badgeRadius}" fill="${badgeColor}" stroke="#ffffff" stroke-width="2"/>` : ''}
</svg>`
  }

  /**
   * Wraps a self-contained marker_config icon (its own background shape + picto already baked in,
   * e.g. a colored circle with a white bolt on top — unlike the monochrome line-icons handled by
   * createMarkerSvgWithIcon, which are tinted white and need our own colored pin behind them) into
   * a shadowed, appropriately-sized standalone marker image. Colors are kept as authored by the
   * backend/admin, so each type d'ouvrage reads as visually distinct instead of every icon forcing
   * the same default pin color.
   * @param iconSvg Raw SVG string of the icon, already carrying its own colors
   * @param targetSize Rendered icon size (width and height) within the output image
   */
  static createStandaloneIconMarker(iconSvg: string, targetSize: number = 64): string {
    const iconViewBox = this.extractViewBox(iconSvg)
    const iconWidth = iconViewBox?.width ?? 64
    const iconHeight = iconViewBox?.height ?? 64

    const shadowOffsetY = 2
    const shadowBlur = 3
    const padding = shadowBlur + shadowOffsetY
    const canvasSize = targetSize + padding * 2

    const shadowFilter = `<defs>
    <filter id="marker-shadow-standalone" x="-50%" y="-50%" width="200%" height="200%">
      <feGaussianBlur in="SourceAlpha" stdDeviation="${shadowBlur}"/>
      <feOffset dx="0" dy="${shadowOffsetY}" result="offsetblur"/>
      <feComponentTransfer>
        <feFuncA type="linear" slope="0.35"/>
      </feComponentTransfer>
      <feMerge>
        <feMergeNode/>
        <feMergeNode in="SourceGraphic"/>
      </feMerge>
    </filter>
  </defs>`

    const iconInner = this.extractSvgInnerContent(iconSvg)

    return `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${canvasSize} ${canvasSize}">
  ${shadowFilter}
  <g filter="url(#marker-shadow-standalone)">
    <svg x="${padding}" y="${padding}" width="${targetSize}" height="${targetSize}" viewBox="0 0 ${iconWidth} ${iconHeight}" preserveAspectRatio="xMidYMid meet">${iconInner}</svg>
  </g>
</svg>`
  }

  /**
   * Best-effort extraction of a marker_config icon's own "identity" color — the first non-white,
   * non-transparent fill found in the SVG, which for these backend-provided badges (a colored
   * background shape + white picto on top) reliably is the background color. Used so cluster
   * bubbles and other UI that need "the icon's color" as a single value can match it without
   * re-deriving it from marker_config (which mostly only sets iconUrl, no markerColor).
   * Returns null when no such color is found (e.g. a monochrome icon) — caller should keep its own
   * fallback color in that case.
   */
  static extractDominantFillColor(svgText: string): string | null {
    const matches = svgText.matchAll(/\bfill="([^"]+)"/g)
    for (const m of matches) {
      const value = m[1].trim().toLowerCase()
      if (!value || value === 'none' || value === 'white' || value === '#fff' || value === '#ffffff') continue
      return m[1].trim()
    }
    return null
  }

  /**
   * Converts SVG string to ImageData for use with MapLibre
   * @param svg SVG string
   * @returns Promise resolving to ImageData
   */
  static async createImageFromSvg(svg: string): Promise<ImageData> {
    return new Promise((resolve, reject) => {
      const img = new Image()
      const svgBlob = new Blob([svg], { type: 'image/svg+xml;charset=utf-8' })
      const url = URL.createObjectURL(svgBlob)

      img.onload = () => {
        // Extract dimensions from SVG viewBox
        const viewBox = this.extractViewBox(svg)
        if (!viewBox) {
          URL.revokeObjectURL(url)
          reject(new Error('Could not extract viewBox from SVG'))
          return
        }

        const width = Math.ceil(viewBox.width)
        const height = Math.ceil(viewBox.height)

        const canvas = document.createElement('canvas')
        canvas.width = width
        canvas.height = height
        const ctx = canvas.getContext('2d')
        if (!ctx) {
          URL.revokeObjectURL(url)
          reject(new Error('Could not get canvas context'))
          return
        }

        // Draw the image scaled to canvas size
        ctx.drawImage(img, 0, 0, width, height)
        const imageData = ctx.getImageData(0, 0, width, height)
        URL.revokeObjectURL(url)
        resolve(imageData)
      }

      img.onerror = () => {
        URL.revokeObjectURL(url)
        reject(new Error('Failed to load SVG image'))
      }

      img.src = url
    })
  }

  /**
   * Fetches raw SVG string from a URL (e.g. SVG Repo download link).
   * @param url Full URL to the SVG file (e.g. https://www.svgrepo.com/download/323640/well.svg)
   * @returns Promise resolving to SVG string
   */
  static async fetchSvgFromUrl(url: string): Promise<string> {
    const response = await fetch(url)
    if (!response.ok) {
      throw new Error(`Failed to fetch SVG: ${response.statusText}`)
    }
    return response.text()
  }

  /**
   * Converts SVG string to data URL for use with MapLibre
   * @param svg SVG string
   * @returns Data URL string
   */
  static svgToDataUrl(svg: string): string {
    return 'data:image/svg+xml;charset=utf-8,' + encodeURIComponent(svg)
  }
}
